import React, { useState, useMemo, useEffect } from 'react';
import { FiPlus, FiVideo, FiAward, FiEdit2, FiTrash2, FiX, FiCheck } from 'react-icons/fi';
import { courseCategoryService, courseService } from '../services/supabaseClient';
import '../styles/Content.css';

// UI keeps friendly 'webinar' / 'certification' labels internally;
// DB (course_type_check constraint) requires these exact strings.
const TYPE_MAP = { webinar: 'unpaid_webinar', certification: 'paid_certification' };
const REVERSE_TYPE_MAP = { unpaid_webinar: 'webinar', paid_certification: 'certification' };

// category is stored/selected by category_id (uuid), not by name —
// course.category_id is a foreign key into course_categories.
const blankForm = { name: '', kind: 'webinar', category_id: '', deliveredBy: 'WiiZ Academy', deliveryDate: '2026-08-18', price: 0, currency: 'INR' };

export default function ContentManagement() {
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState('');
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [editId, setEditId] = useState(null);

  // DB CONNECTED: load categories + courses together on mount
  useEffect(() => {
    let cancelled = false;

    courseCategoryService.getAll()
      .then(rows => { if (!cancelled) setCategories(rows); })
      .catch(() => { if (!cancelled) setCategories([]); });

    courseService.getAll()
      .then(rows => { if (!cancelled) setRows(rows); })
      .catch(() => { if (!cancelled) setRowsError('Could not load content.'); })
      .finally(() => { if (!cancelled) setRowsLoading(false); });

    return () => { cancelled = true; };
  }, []);

  // counts + tab filter compare against DB type values, not UI labels
  const counts = {
    all: rows.length,
    webinar: rows.filter(r => r.type === 'unpaid_webinar').length,
    cert: rows.filter(r => r.type === 'paid_certification').length
  };

  const filteredRows = useMemo(() => {
    if (activeTab === 'all') return rows;
    const dbType = activeTab === 'webinar' ? 'unpaid_webinar' : 'paid_certification';
    return rows.filter(r => r.type === dbType);
  }, [rows, activeTab]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      // guard against category list not being loaded yet
      setForm({ ...blankForm, category_id: categories[0]?.category_id ?? '' });
      setEditId(null);
    }
  };

  const handleEdit = (row) => {
    // map DB row -> form shape (DB uses type/category_id/delivered_by/delivery_date,
    // and DB 'type' values need converting back to the UI's 'webinar'/'certification')
    setForm({
      name: row.name,
      kind: REVERSE_TYPE_MAP[row.type] || row.type,
      category_id: row.category_id,
      deliveredBy: row.delivered_by,
      deliveryDate: row.delivery_date,
      price: row.price,
      currency: row.currency
    });
    setEditId(row.course_id);
    setIsModalOpen(true);
  };

  // DB CONNECTED: create/update against the `course` table
  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.category_id) {
      setRowsError('Please select a category before saving.');
      return;
    }

    const payload = {
      name: form.name,
      type: TYPE_MAP[form.kind],
      category_id: form.category_id,
      delivered_by: form.deliveredBy,
      delivery_date: form.deliveryDate,
      price: form.kind === 'webinar' ? 0 : Number(form.price),
      currency: form.currency,
      status: 'active',       // matches course_status_check
      source: 'admin'         // matches course_source_check (lowercase)
    };

    try {
      if (editId) {
        const updated = await courseService.update(editId, payload);
        setRows(prev => prev.map(r => r.course_id === editId ? updated : r));
      } else {
        // course_code is cosmetic, kept client-side just like before
        const prefix = form.kind === 'webinar' ? 'WZ-C' : 'WZ-X';
        const created = await courseService.add({
          ...payload,
          course_code: `${prefix}-${String(rows.length + 1).padStart(4, '0')}`
        });
        setRows(prev => [...prev, created]);
      }
      setIsModalOpen(false);
      setRowsError('');
    } catch (err) {
      setRowsError(err.message || 'Could not save content.');
    }
  };

  // DB CONNECTED: delete with optimistic UI + rollback on failure
  const handleDelete = async (row) => {
    const prevRows = rows;
    setRows(prev => prev.filter(r => r.course_id !== row.course_id));
    try {
      await courseService.remove(row.course_id);
    } catch (err) {
      setRows(prevRows);
      setRowsError('Could not remove content — please retry.');
    }
  };

  return (
    <div className="page">
      <div className="hero">
        <div>
          <span className="eyebrow" style={{ color: 'var(--color-primary)' }}>Course & certificate</span>
          <h2>Courses & Certifications</h2>
          <p>Unpaid webinars issue instantly. Paid certifications are gated on payment.</p>
        </div>
        <button className="primary" onClick={toggleModal} style={{ borderRadius: '8px', padding: '10px 20px' }}>
          <FiPlus size={14} style={{ marginRight: 6 }} /> Add Course & certificate
        </button>
      </div>

      <div className="tabs">
        <button className={activeTab === 'all' ? 'active' : ''} onClick={() => setActiveTab('all')}>All <span>{counts.all}</span></button>
        <button className={activeTab === 'webinar' ? 'active' : ''} onClick={() => setActiveTab('webinar')}>Webinars (unpaid) <span>{counts.webinar}</span></button>
        <button className={activeTab === 'certification' ? 'active' : ''} onClick={() => setActiveTab('certification')}>Certifications (paid) <span>{counts.cert}</span></button>
      </div>

      <div className="panel" style={{ borderRadius: '0 0 12px 12px' }}>
        {rowsError && <div className="error">{rowsError}</div>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th><th>NAME</th><th>KIND</th><th>CATEGORY</th><th>PRICE</th><th>DELIVERY</th><th>DELIVERED BY</th><th>SOURCE</th><th style={{ textAlign: 'right' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {rowsLoading && (
                <tr><td colSpan={9} className="empty">Loading content…</td></tr>
              )}
              {!rowsLoading && filteredRows.map(row => (
                <tr key={row.course_id}>
                  <td className="mono" style={{ opacity: 0.5 }}>{row.course_code}</td>
                  <td style={{ fontWeight: '750', color: 'var(--text-primary)' }}>{row.name}</td>
                  <td>
                    <span
                      className="status"
                      style={row.type === 'unpaid_webinar'
                        ? { background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }
                        : { background: 'var(--color-primary-lightest)', color: 'var(--color-primary)' }}
                    >
                      {(REVERSE_TYPE_MAP[row.type] || row.type).toUpperCase()}
                    </span>
                  </td>
                  {/* category name resolved from the join, fallback to '—' */}
                  <td>{row.course_categories?.name || '—'}</td>
                  <td><div className="price-cell"><b>{row.price === 0 ? 'Free' : `₹${row.price}`}</b>{row.price > 0 && <span className="price-subtext">WiiZ collects</span>}</div></td>
                  <td>{row.delivery_date}</td><td>{row.delivered_by}</td><td><span className="source-chip">{row.source}</span></td>
                  <td className="actions" style={{ justifyContent: 'flex-end' }}>
                    <button onClick={() => handleEdit(row)}><FiEdit2 size={10} /></button>
                    <button onClick={() => handleDelete(row)} className="danger"><FiTrash2 size={10} /></button>
                  </td>
                </tr>
              ))}
              {!rowsLoading && !filteredRows.length && (
                <tr><td colSpan={9} className="empty">No content yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="overlay">
          <div className="modal">
            <div className="modal-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                <div style={{ width: '40px', height: '40px', background: 'var(--color-primary-lightest)', color: 'var(--color-primary)', display: 'grid', placeItems: 'center', borderRadius: '8px' }}><FiAward size={20} /></div>
                <div><h2 style={{ fontSize: '16px', margin: 0 }}>{editId ? 'Edit Content' : 'Add Content'}</h2><p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>Unpaid webinar or paid certification.</p></div>
              </div>
              <FiX style={{ cursor: 'pointer', color: 'var(--text-muted)' }} onClick={toggleModal} size={20} />
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body">
                <span className="eyebrow" style={{ color: 'var(--color-primary)', marginBottom: '15px', display: 'block' }}>CONTENT TYPE — DRIVES THE ENTIRE FLOW</span>
                <div className="type-grid">
                  <div className={`type-card ${form.kind === 'webinar' ? 'active' : ''}`} onClick={() => setForm({ ...form, kind: 'webinar', price: 0 })}>
                    {form.kind === 'webinar' && <FiCheck className="type-check" size={14} />}
                    <b>Unpaid Webinar</b><p>Free. Auto-issues at registration. Never enters an approval queue.</p>
                  </div>
                  <div className={`type-card ${form.kind === 'certification' ? 'active' : ''}`} onClick={() => setForm({ ...form, kind: 'certification' })}>
                    {form.kind === 'certification' && <FiCheck className="type-check" size={14} />}
                    <b>Paid Certification</b><p>Payment-gated. Cannot release until payment is confirmed.</p>
                  </div>
                </div>

                <span className="eyebrow" style={{ color: 'var(--color-primary)', marginBottom: '15px', display: 'block' }}>DETAILS</span>
                <div className="modal-form-grid">
                  <label><span className="form-label">Name *</span><input className="modal-input" required placeholder="Enter content name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
                  <div className="grid-2">
                    <label>
                      <span className="form-label">Category *</span>
                      <select
                        className="modal-input"
                        value={form.category_id}
                        onChange={e => setForm({ ...form, category_id: e.target.value })}
                      >
                        {categories.length === 0 && <option value="">No categories yet</option>}
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                        ))}
                      </select>
                    </label>
                    <label><span className="form-label">Delivered by *</span><input className="modal-input" value={form.deliveredBy} onChange={e => setForm({ ...form, deliveredBy: e.target.value })} /></label>
                  </div>
                  <div className="grid-3">
                    <label><span className="form-label">Delivery date *</span><input className="modal-input" type="date" value={form.deliveryDate} onChange={e => setForm({ ...form, deliveryDate: e.target.value })} /></label>
                    <label><span className="form-label">Price</span><input className="modal-input" type="number" disabled={form.kind === 'webinar'} value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} style={{ opacity: form.kind === 'webinar' ? 0.5 : 1 }} /></label>
                    <label><span className="form-label">Currency</span><select className="modal-input" disabled={form.kind === 'webinar'} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={{ opacity: form.kind === 'webinar' ? 0.5 : 1 }}><option>INR</option><option>USD</option></select></label>
                  </div>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="secondary" onClick={toggleModal} style={{ borderRadius: '8px' }}>Cancel</button>
                <button type="submit" className="primary" style={{ background: 'var(--color-primary)', borderRadius: '8px' }}>Save content</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}